import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import dayjs from 'dayjs';
import ParityTooltipsTooltipPosTableLoss from 'lumen/components/parity/tooltips/tooltip-pos-table-loss';
import parityClass from 'lumen/helpers/parity-class';
import { filterMajorOTAs, filterParities } from 'lumen/utils/parities-filter';
import { formatParity, sortParities } from 'lumen/utils/parity-utils';

interface ParityTablesCellsTdPosLowestRateOverOtasSignature {
  Args: {
    cellData: unknown;
    column: unknown;
  };
}

export default class ParityTablesCellsTdPosLowestRateOverOtas extends Component<ParityTablesCellsTdPosLowestRateOverOtasSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRates() {
    const { cellData, column } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      otaIds,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      cellData.paritiesPOSDict,
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) => {
      const { channel, currency, extract_datetime, ota } = baseRate;

      return {
        channel: channel as IconBrandName,
        extractDatetimeFromNow: dayjs.utc(extract_datetime).fromNow(),
        extract_datetime,
        rate: this.rates.formatRate(baseRate, this.hotel),
        rateUnconverted: [
          this.rates.formatRateObject(baseRate, false),
          currency,
        ].join(' '),
        source: this.ota.otaIdToPrettyName(ota),
        title: this.ota.otaIdToPrettyName(channel),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannels() {
    // @ts-expect-error: Incorrect type
    const res = [];

    // @ts-expect-error: Incorrect type
    if (!this.period.paritiesPOSDict) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return res;
    }

    const parities = filterParities(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.period.paritiesPOSDict,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.column.searchMajorOtas,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.args.column.availableOTAs,
      (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
    );

    return parities
      .sort(sortParities)
      .slice(0, 15)
      .map((parity) => formatParity(this.ota, this.rates, this.hotel, parity));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.period.day.isToday;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lowestOTARateObject() {
    let lowestOTARateObject = null;

    // @ts-expect-error: Incorrect type
    if (this.period.paritiesPOSDict) {
      // @ts-expect-error: Incorrect type
      let lowestOTARate = null;
      // used to check if the ota is in OTAsParity
      const parities = filterParities(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.period.paritiesPOSDict,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.column.searchMajorOtas,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.column.availableOTAs,
        (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
      );

      // Loop over parities to see who is the lowest
      parities.forEach((parity) => {
        // @ts-expect-error: Incorrect type
        if (!lowestOTARate || parity.value < lowestOTARate) {
          lowestOTARate = parity.value;
          lowestOTARateObject = parity;
        }
      });
    }

    return lowestOTARateObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lowestOTARateValue() {
    if (this.lowestOTARateObject) {
      return `${this.rates.formatRateObject(this.lowestOTARateObject, false)} ${
        // @ts-expect-error: Incorrect type
        this.lowestOTARateObject?.currency
      }`;
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  <template>
    <div class="truncate tabular-nums {{if this.isToday 'td-today'}}">
      <span
        {{! @glint-expect-error: Incorrect type }}
        class={{parityClass this.lowestOTARateObject}}
        role="tooltip"
        title={{this.lowestOTARateValue}}
      >
        {{this.lowestOTARateValue}}
        {{#if this.lowestOTARateObject}}
          <ParityTooltipsTooltipPosTableLoss
            @baseRates={{this.baseRates}}
            @formattedChannels={{this.formattedChannels}}
          />
        {{/if}}
      </span>
    </div>
  </template>
}
